import * as d3 from "d3";
import { nearestDate } from "../../utils/dateFunctions";

export function draw(
  container,
  data,
  pxRange,
  brushRef,
  setBrushRef,
  setActiveLine,
  setTooltip,
  config,
  setForceRefresh
) {
  const cwidth = container.clientWidth;
  const cheight = container.clientHeight;

  const {
    activeLine,
    axes,
    border,
    brush,
    header,
    layout,
    opacityMapping,
    tooltips,
    svg: svgx,
    horizontalLines,
    verticalLines,
  } = config;

  const marginBottom =
    layout.margin.bottom + (brush.x.active ? brush.x.width : 0);
  const marginLeft = layout.margin.left + (brush.y.active ? brush.y.width : 0);

  const svg = d3
    .select(container)
    .on("click", function (e) {
      const el = d3.select(e.target).node().nodeName;
      if (el === "svg") {
        if (activeLine.active) {
          setActiveLine("");
        }
      }
    })
    .style("cursor", activeLine.active ? "pointer" : undefined);
  svg.selectAll("g").remove();
  svg.selectAll("clipPath").remove();

  if (data) {
    /** d3 margin convention */
    const width = cwidth - marginLeft - layout.margin.right;
    const height = cheight - layout.margin.top - marginBottom;
    /** SVG Styles */
    svg.style("background-color", svgx.Background);
    if (svgx.border) {
      svg.style("border", svgx.border);
    }

    let clipped = {};

    if (header) {
      const headerGroup = svg.append("g");

      headerGroup
        .append("text")
        .attr("y", header.margin.top)
        .attr("x", header.margin.left)
        .attr("fill", header.colour)
        .style("text-anchor", "left")
        .style("font-size", header.fontSize)
        .style("font-weight", header.fontWeight)
        .text(header.text);
    }

    const maskname = `mask${Math.random().toString().split(".").join("")}`;

    if (brush.x.active || brush.y.active) {
      svg
        .append("clipPath")
        .attr("id", maskname)
        .style("pointer-events", "none")
        .append("rect")
        .attr("x", 0)
        .attr("y", 0)
        .attr("width", width)
        .attr("height", height);

      clipped = svg
        .append("g")
        .attr("clip-path", `url(#${maskname})`)
        .attr(
          "transform",
          "translate(" + marginLeft + "," + layout.margin.top + ")"
        );
    } else {
      clipped = svg
        .append("g")
        .attr(
          "transform",
          "translate(" + marginLeft + "," + layout.margin.top + ")"
        );
    }

    const lineGroup = clipped.append("g");
    const horizontalLinesGroup = clipped.append("g");

    clipped
      .append("rect")
      .attr("width", width)
      .attr("height", height)
      .attr("fill", "none")
      .attr("stroke", border.stroke)
      .attr("stroke-width", border.strokeWidth);

    /* Format Data */
    const parseDate = d3.timeParse("%m/%d/%Y");
    const formatTime = d3.timeFormat("%m/%d/%Y");

    const linedata = data;

    if (linedata.length > 0) {
      const xScale = d3
        .scaleTime()
        .domain(
          d3.extent(
            linedata.reduce((a, b) => {
              return (a = a.concat(b.values));
            }, []),
            (d) => d.date
          )
        )
        .range([0, width]);

      const refxScale = d3
        .scaleTime()
        .domain(
          d3.extent(
            linedata.reduce((a, b) => {
              return (a = a.concat(b.values));
            }, []),
            (d) => d.date
          )
        )
        .range([0, width]);

      const yScale = d3
        .scaleLinear()
        .domain([pxRange.min, pxRange.max])
        .range([height, 0]);

      const y = d3
        .scaleLinear()
        .domain([pxRange.min, pxRange.max])
        .range([height, 0]);

      /* Add line into SVG */
      var line = d3
        .line()
        .x((d) => xScale(d.date))
        .y((d) => yScale(d.px));

      const onMouseover = (x, y, e, d) => {
        if (tooltips.active) {
          const targetDate = xScale.invert(x);
          const dates = d.values.map((item) => item.date);
          const keyDate = nearestDate(dates, targetDate);
          const closestItem = d.values[keyDate];

          const content = {
            "Vector Set": closestItem.vectorSet,
            "Vec Level": closestItem.vecLevel,
            "Vec Dates": closestItem.vecDates,
            "Vec Events": closestItem.vecEvents,
            Date: formatTime(closestItem.date),
            Px: closestItem.px,
          };
          const objTooltip = {
            active: true,
            position: { x: e.pageX, y: e.pageY },
            content: content,
            stroke: d.stroke,
          };
          setTooltip(objTooltip);
        }
      };

      lineGroup
        .selectAll(".line-group")
        .data(linedata)
        .enter()
        .append("g")
        .attr("class", "line-group")
        .append("path")
        .attr("class", "line")
        .attr("d", (d) => {
          return line(d.values);
        })
        .style("stroke", (d, i) => d.stroke)
        .style("stroke-width", (d, i) => d.strokeWidth)
        .style("opacity", (d, i) => {
          return (100 - opacityMapping[d.vStrength.trim()].Fade) / 100;
        })
        .style("fill", "none")
        .style("cursor", () => (activeLine.active ? "pointer" : undefined))
        .on("click", (e, d) => {
          if (activeLine.active) {
            setActiveLine(d.name);
          }
        })
        .on("mouseover", function (e, d) {
          const [x, y] = d3.pointer(e);
          onMouseover(x, y, e, d);
        })
        .on("mouseleave", function (e) {
          const objTooltip = {
            active: false,
            position: { x: 200, y: 200 },
            content: {},
            stroke: "",
          };
          setTooltip(objTooltip);
        });

      /* Add Axis into SVG */
      var xAxis = d3
        .axisBottom(xScale)
        .ticks(5)
        .tickSizeInner(axes.x.tickSizeInner);
      var yAxis = d3
        .axisLeft(yScale)
        .ticks(5)
        .tickSizeInner(axes.y.tickSizeInner);

      svg
        .append("g")
        .attr("class", "x axis")
        .attr(
          "transform",
          `translate(${marginLeft}, ${height + layout.margin.top})`
        )
        .call(xAxis);

      if (axes.y.show) {
        svg
          .append("g")
          .attr("class", "y axis")
          .call(yAxis)
          .attr("transform", `translate(${marginLeft}, ${layout.margin.top})`);
      }

      svg.selectAll(".x .domain").style("stroke", axes.x.domainColour);
      svg.selectAll(".x .tick line").style("stroke", axes.x.tickLineColour);
      svg.selectAll(".x .tick text").style("fill", axes.x.textColour);
      svg.selectAll(".y .domain").style("stroke", axes.y.domainColour);
      svg.selectAll(".y .tick line").style("stroke", axes.y.tickLineColour);
      svg.selectAll(".y .tick text").style("fill", axes.y.textColour);

      if (verticalLines) {
        const verticalLineGroup = svg
          .append("g")
          .attr(
            "transform",
            "translate(" + marginLeft + "," + layout.margin.top + ")"
          );

        verticalLineGroup
          .selectAll(".vline")
          .data(Object.entries(verticalLines))
          .enter()
          .append("line")
          .attr("class", "vline")
          .attr("x1", (d) => xScale(parseDate(d[0])))
          .attr("x2", (d) => xScale(parseDate(d[0])))
          .attr("y1", 0)
          .attr("y2", (d) => height + d[1].line.extendBelow)
          .attr("stroke", (d) => d[1].line.stroke)
          .attr("stroke-width", (d) => d[1].line.strokeWidth);

        verticalLineGroup
          .selectAll(".lineText")
          .data(Object.entries(verticalLines))
          .enter()
          .append("text")
          .attr("class", "lineText")
          .attr("y", height)
          .attr("dy", (d) => d[1].label.dy)
          .attr("dx", (d) => d[1].label.dx)
          .attr("x", (d) => xScale(parseDate(d[0])))
          .attr("fill", (d) => d[1].label.colour)
          .style("text-anchor", (d) => d[1].label.textAnchor)
          .style("font-size", (d) => d[1].label.fontSize)
          .style("font-weight", (d) => d[1].label.fontWeight)
          .text((d) => d[0]);
      }

      if (horizontalLines) {
        horizontalLinesGroup
          .selectAll(".hline")
          .data(Object.entries(horizontalLines))
          .enter()
          .append("line")
          .attr("class", "hline")
          .attr("x1", (d) => 0)
          .attr("x2", (d) => width)
          .attr("y1", (d) => yScale(d[0]))
          .attr("y2", (d) => yScale(d[0]))
          .attr("stroke", (d) => d[1].line.stroke)
          .attr("stroke-width", (d) => d[1].line.strokeWidth);

        horizontalLinesGroup
          .selectAll(".hlineText")
          .data(Object.entries(horizontalLines))
          .enter()
          .filter((d) => "label" in d[1])
          .append("text")
          .attr("class", "hlineText")
          .attr("y", (d) => yScale(d[0]))
          .attr("dy", (d) => d[1].label.dy)
          .attr("dx", (d) => d[1].label.dx)
          .attr("x", (d) => 0)
          .attr("fill", (d) => d[1].label.colour)
          .style("text-anchor", (d) => d[1].label.textAnchor)
          .style("font-size", (d) => d[1].label.fontSize)
          .style("font-weight", (d) => d[1].label.fontWeight)
          .text((d) => d[0]);
      }

      if (brush.x.active) {
        const xbrushed = (event) => {
          const s = event.selection || xScale.domain();
          if (setBrushRef) {
            setBrushRef(
              s.map((v) => v / width),
              "x"
            );
          }
          xScale.domain(s.map(refxScale.invert, refxScale));
          svg.select(".x").call(xAxis);

          lineGroup.selectAll(".line-group").remove();
          lineGroup
            .selectAll(".line-group")
            .data(linedata)
            .enter()
            .append("g")
            .attr("class", "line-group")
            .append("path")
            .attr("class", "line")
            .attr("d", (d) => line(d.values))
            .style("stroke", (d, i) => d.stroke)
            .style("stroke-width", (d, i) => d.strokeWidth)
            .style("opacity", (d, i) => {
              return (100 - opacityMapping[d.vStrength.trim()].Fade) / 100;
            })
            .style("cursor", () => (activeLine.active ? "pointer" : undefined))
            .style("fill", "none")
            .on("click", (e, d) => {
              if (activeLine.active) {
                setActiveLine(d.name);
              }
            })
            .on("mouseover", function (e, d) {
              const [x, y] = d3.pointer(e);
              onMouseover(x, y, e, d);
            })
            .on("mouseout", function (e) {
              const objTooltip = {
                active: false,
                position: { x: 200, y: 200 },
                content: {},
                stroke: "",
              };
              setTooltip(objTooltip);
            });
        };

        const xBrush = d3
          .brushX()
          .extent([
            [0, 0],
            [width, brush.x.width],
          ])
          .on("brush", xbrushed);

        svg
          .append("g")
          .attr("class", "xbrush")
          .attr(
            "transform",
            `translate(${marginLeft}, ${
              cheight - brush.x.width - brush.x.margin
            })`
          )
          .call(xBrush)
          .call(
            xBrush.move,
            brushRef.x.length > 0
              ? brushRef.x.map((v) => v * width)
              : [0, width]
          );
      }
      if (brush.y.active) {
        const ybrushed = (event) => {
          const s = event.selection || yScale.domain();
          if (setBrushRef) {
            setBrushRef(
              s.map((v) => v / height),
              "y"
            );
          }
          yScale.domain(s.map(y.invert, y).reverse());
          svg.select(".y").call(yAxis);

          lineGroup.selectAll(".line-group").remove();
          lineGroup
            .selectAll(".line-group")
            .data(linedata)
            .enter()
            .append("g")
            .attr("class", "line-group")
            .append("path")
            .attr("class", "line")
            .attr("d", (d) => line(d.values))
            .style("stroke", (d, i) => d.stroke)
            .style("stroke-width", (d, i) => d.strokeWidth)
            .style("opacity", (d, i) => {
              return (100 - opacityMapping[d.vStrength.trim()].Fade) / 100;
            })
            .style("fill", "none")
            .style("cursor", () => (activeLine.active ? "pointer" : undefined))
            .on("click", (e, d) => {
              if (activeLine.active) {
                setActiveLine(d.name);
              }
            })
            .on("mouseover", function (e, d) {
              const [x, y] = d3.pointer(e);
              onMouseover(x, y, e, d);
            })
            .on("mouseout", function (e) {
              const objTooltip = {
                active: false,
                position: { x: 200, y: 200 },
                content: {},
                stroke: "",
              };
              setTooltip(objTooltip);
            });

          horizontalLinesGroup.selectAll(".hline").remove();
          horizontalLinesGroup
            .selectAll(".hline")
            .data(Object.entries(horizontalLines))
            .enter()
            .append("line")
            .attr("class", "hline")
            .attr("x1", (d) => 0)
            .attr("x2", (d) => width)
            .attr("y1", (d) => yScale(d[0]))
            .attr("y2", (d) => yScale(d[0]))
            .attr("stroke", (d) => d[1].line.stroke)
            .attr("stroke-width", (d) => d[1].line.strokeWidth);

          horizontalLinesGroup.selectAll(".hlineText").remove();

          horizontalLinesGroup
            .selectAll(".hlineText")
            .data(Object.entries(horizontalLines))
            .enter()
            .filter((d) => "label" in d[1])
            .append("text")
            .attr("class", "hlineText")
            .attr("y", (d) => yScale(d[0]))
            .attr("dy", (d) => d[1].label.dy)
            .attr("dx", (d) => d[1].label.dx)
            .attr("x", (d) => 0)
            .attr("fill", (d) => d[1].label.colour)
            .style("text-anchor", (d) => d[1].label.textAnchor)
            .style("font-size", (d) => d[1].label.fontSize)
            .style("font-weight", (d) => d[1].label.fontWeight)
            .text((d) => d[0]);
          /////////
          if (setForceRefresh) {
            setForceRefresh();
          }
        };

        const yBrush = d3
          .brushY()
          .extent([
            [0, 0],
            [brush.y.width, height],
          ])
          .on("brush", ybrushed);
        svg
          .append("g")
          .attr("class", "brush")
          .attr(
            "transform",
            `translate(${brush.y.margin}, ${layout.margin.top})`
          )
          .call(yBrush)
          .call(
            yBrush.move,
            brushRef.y.length > 0
              ? brushRef.y.map((v) => v * height)
              : [0, height]
          );
      }
    }

    svg.node().focus();
  }
}
